import { useEffect, useRef } from 'react';
import { escalaDoOrbe, suavizar } from './nivel';

/**
 * O orbe que respira com a voz.
 *
 * O nível chega ~12 vezes por segundo (bloco de 4096 amostras a 48 kHz). No
 * `BotaoDeDitado` isso vira `setState` sem dor porque o componente é minúsculo;
 * aqui a mesma coisa repintaria a conversa inteira doze vezes por segundo, com
 * a transcrição crescendo junto. Então **o nível não entra em estado do React**:
 * ele vai por `ref` para uma propriedade CSS, e quem anima é o compositor do
 * navegador.
 *
 * Só `transform` e `opacity` se mexem — nunca `filter` nem `box-shadow`, que
 * repintam a cada quadro e comem bateria de celular. O gradiente é pintado uma
 * vez, no fundo.
 */
export function Orbe({
  nivel,
  ouvindo,
}: {
  /** RMS cru do gravador, 0..~0,2. Zero quando não há microfone aberto. */
  nivel: number;
  /** Aceso enquanto grava; parado, o orbe apenas respira devagar. */
  ouvindo: boolean;
}): React.JSX.Element {
  const no = useRef<HTMLDivElement>(null);
  const alisado = useRef(0);

  useEffect(() => {
    alisado.current = suavizar(alisado.current, ouvindo ? nivel : 0);
    // Escrita direta no DOM: sem render, sem reconciliação, sem tocar na
    // conversa que está desenhada ao lado.
    no.current?.style.setProperty('--nivel', String(escalaDoOrbe(alisado.current)));
  }, [nivel, ouvindo]);

  return (
    <div
      ref={no}
      aria-hidden
      style={{ '--nivel': 1 } as React.CSSProperties}
      className={
        'h-40 w-40 shrink-0 rounded-full ' +
        // `scale-[var(--nivel)]` e o que liga a propriedade CSS ao tamanho.
        // Sem esta classe o `--nivel` mudaria a cada quadro e NADA se moveria:
        // um no-op silencioso, que e o pior tipo de defeito de tela.
        'scale-[var(--nivel)] ' +
        'bg-[radial-gradient(circle_at_30%_25%,#a78bfa_0%,#7c3aed_35%,#4c1d95_70%,#1e1b4b_100%)] ' +
        'shadow-[0_0_60px_-10px_rgba(124,58,237,0.7)] ' +
        // A transição de 75 ms é a mesma da barra de `audio.gravar.tsx`: os
        // dois medidores da casa têm de se mover no mesmo compasso.
        'transition-transform duration-75 ease-linear ' +
        'motion-reduce:transition-none ' +
        (ouvindo ? 'opacity-100' : 'opacity-70')
      }
      // `scale(var(--nivel))` e não `style={{transform}}`: assim o valor muda
      // sem o React reescrever o atributo.
      data-orbe={ouvindo ? 'ouvindo' : 'parado'}
    />
  );
}
